import { useMemo, useEffect } from 'react';
import { noop } from 'lodash';
import { setLogger, useQuery } from 'react-query';
import { logger } from '@fiverr-private/obs';
import { useUser } from '../../context/user/userProvider';
import { metricsService } from '../../../utils/metrics';
import { IdentifiedJobFullData, IdentifiedJobInfo } from '../../../lib/interfaces/jobsTBD';
import { getLoadDataFunctions } from './JobsTbdContainer.utils';

export const useJobData = (
    jobInitialData: IdentifiedJobInfo,
    rollouts: Record<string, boolean>
): IdentifiedJobFullData | IdentifiedJobInfo => {
    const userContext = useUser();
    const { loadData, loadFallbackData } = getLoadDataFunctions(jobInitialData, userContext, rollouts);

    // Muting the default react-query logs
    setLogger({
        log: noop,
        warn: noop,
        error: noop,
    });

    const query = useQuery(jobInitialData.jobType, loadData, {
        retry: false,
        refetchOnWindowFocus: false,
    });

    const result = useMemo(() => {
        const defaultResult = jobInitialData;

        if (query.isLoading) {
            return defaultResult;
        }

        if (query.isSuccess) {
            return {
                jobType: jobInitialData.jobType,
                jobData: query.data,
            };
        }

        if (query.isError) {
            const data = loadFallbackData();

            return {
                jobType: jobInitialData.jobType,
                jobData: data,
            };
        }

        return defaultResult;
    }, [query.isLoading, query.isSuccess, query.isError, query.data, jobInitialData, loadFallbackData]);

    useEffect(() => {
        if (query.isSuccess) {
            metricsService.count(`jtbd.${jobInitialData.jobType}.client.load.success`);
        } else if (query.isError) {
            logger.warn(query.error as any, {
                description: 'Failed to load job data',
                jobType: jobInitialData.jobType,
            });
            metricsService.count(`jtbd.${jobInitialData.jobType}.client.load.failed.reason.other`);
        }
    }, [query.isSuccess, query.isError, jobInitialData.jobType, query.error]);

    return result;
};

export const useJobsData = (
    jobsInitialData: IdentifiedJobInfo[],
    rollouts: Record<string, boolean>
): (IdentifiedJobFullData | IdentifiedJobInfo)[] =>
    jobsInitialData.map((jobInitialData) => useJobData(jobInitialData, rollouts));
